import type { ReactNode } from 'react';
import type { Signal, SignalKind } from './signals';
import './insights.css';
import './account.css';

const KIND: Record<SignalKind, { icon: string; word: string }> = {
  risk: { icon: '⚠', word: 'Risk' },
  billing: { icon: '$', word: 'Billing' },
  upgrade: { icon: '↑', word: 'Upgrade' },
  adoption: { icon: '◎', word: 'Adoption' },
};

export interface SignalsStripProps {
  signals: readonly Signal[];
  /** Writes the signal's next step; absent for a role that cannot write notes. */
  onStep?: ((signal: Signal) => void) | undefined;
  busy?: boolean;
}

/**
 * What to do about the account (round 2, sales M3, dataviz N3): compact chips, the most money
 * first, each with its kind in a word and a glyph, its money a month, a link to the evidence and
 * one action, "Next step", which writes the signal's step down. Signals a hero tile already says
 * (the runway) are left out by the page.
 */
export function SignalsStrip({ signals, onStep, busy }: SignalsStripProps): ReactNode {
  if (signals.length === 0) {
    return <p className="ad-chips ad-chips--none">No signals: nothing to call about.</p>;
  }
  return (
    <ul className="ad-chips" aria-label="Signals">
      {signals.map((s) => (
        <li key={s.text} className={`ad-chip ad-chip--${s.kind}`}>
          <a href={s.href}>
            <span className="ad-chip__kind">
              <span aria-hidden="true">{KIND[s.kind].icon}</span> {KIND[s.kind].word}
            </span>{' '}
            {s.text}
          </a>
          {s.value ? <span className="ad-chip__value">{s.value}</span> : null}
          {onStep ? (
            <button
              type="button"
              className="ad-chip__act"
              disabled={busy}
              onClick={() => onStep(s)}
              title={s.step}
            >
              Next step
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
